import React from 'react';
import { navigate } from 'gatsby';
import { QueryParamProvider } from 'use-query-params';
import { sendGAFormEvent } from './src/components/trialForm/utils/trialFormUtils';
import {
  latchTrialNovalidateFromWindow,
  promoteTrialNovalidateFromSearch
} from './src/components/trialForm/utils/novalidateQuery';
import { isBrowser } from './src/utilities/misc';
import {
  startThirdPartySnippets,
  handleThirdPartyRouteChange
} from './src/utilities/third-party';

const historyInterface = {
  push: ({ pathname, search, state }) => {
    navigate(`${pathname}${search}`, { state, replace: false });
  },
  replace: ({ pathname, search, state }) => {
    navigate(`${pathname}${search}`, { state, replace: true });
  },
};

const locationWithClientSearch = (location) => {
  if (typeof window === 'undefined' || location.search || !window.location.search) {
    return location;
  }
  // Gatsby page props often have empty search on first hydrate; QueryParamProvider
  // would then replace the real `?novalidate=1` off the address bar.
  return { ...location, search: window.location.search };
};

export const wrapPageElement = ({ element, props: { location } }) => (
  <QueryParamProvider history={historyInterface} location={locationWithClientSearch(location)}>
    {element}
  </QueryParamProvider>
);

export const onClientEntry = () => {
  latchTrialNovalidateFromWindow();
  // Start the third-party snippet manager (Intercom, Optimizely, ...). This sets
  // up the first-interaction bridge so Intercom still waits for engagement
  // before loading, now from site code instead of GTM.
  startThirdPartySnippets();
};

export const shouldUpdateScroll = ({ routerProps: { location } }) => {
  if (location.hash || location.state?.disableScroll === true) {
    return false;
  }

  return true;
};

export const onRouteUpdate = ({ location, prevLocation }) => {
  const hasPrevLocation = prevLocation !== null;
  promoteTrialNovalidateFromSearch(location?.search);
  // Drive the third-party snippet manager on every route change. This loads
  // Intercom on the chat/trial pages, keeps its Product attribute in sync, and
  // opens the messenger on `#open-chat` deep links (replacing the old GTM
  // route-change + "Intercom - Show" tags).
  if (isBrowser) handleThirdPartyRouteChange(location);
  if (isBrowser) {
    // for trial form, we store the return target in sessionStorage
    // then get it out here and compare the current pathname with the stored 'from' pathname
    // if they match (user navigated back to the previous page), we send the GA Form event
    const currentPath = location.pathname;
    const storedTarget = sessionStorage.getItem('trial:returnTarget');
    let storedTargetObject = null;
    try {
      storedTargetObject = storedTarget ? JSON.parse(storedTarget) : null;
    } catch (e) {
      sessionStorage.removeItem('trial:returnTarget');
    }
    if (storedTargetObject?.from?.length > 0
      && storedTargetObject?.to?.length > 0
      && currentPath === storedTargetObject.from) {
      console.log('form abandoned browser back clicked:::');
      sendGAFormEvent('form abandoned browser back clicked', storedTargetObject.variation);

      sessionStorage.removeItem('trial:returnTarget');
    }
  }

  if (isBrowser && location.hash && (location.state?.disableScroll !== true || !hasPrevLocation)) {
    const element = document.getElementById(location.hash.substring(1));
    if (element !== null) {
      window.setTimeout(() => {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 250);
    }
  }
};
